import moment from "moment";

export const errorGenerator = (field) => {
  return `${field} is required!`;
};

export function replaceFirstTwoLetters(inputString, replacement) {
  if (inputString.length >= 2) {
    return replacement + inputString.substring(2);
  } else {
    return inputString; // Handle cases where the string is shorter than 2 characters
  }
}

export function getTimeDifferenceMessage(targetDate) {
  const currentDate = new Date();
  const timeDifference = currentDate - targetDate;
  const hoursDifference = Math.floor(timeDifference / (1000 * 60 * 60));
  const daysDifference = Math.floor(hoursDifference / 24);
  const monthsDifference = Math.floor(daysDifference / 30);
  const yearsDifference = Math.floor(monthsDifference / 12);

  if (hoursDifference < 24) {
    if (hoursDifference < 1) {
      return "Less than 1 hour ago";
    }
    return `${hoursDifference} hours ago`;
  } else if (daysDifference < 30) {
    return `${daysDifference} days ago`;
  } else if (monthsDifference < 12) {
    return `${monthsDifference} months ago`;
  } else {
    return `${yearsDifference} years ago`;
  }
}

export function removeDuplicates(array) {
  return array.filter((value, index, self) => {
    // Return true if the value's index is the first occurrence of that value in the array
    return self.indexOf(value) === index;
  });
}

export function cutAndAppendEllipsis(inputString, maxLength = 20) {
  if (inputString.length > maxLength) {
    return inputString.slice(0, maxLength - 3) + "...";
  } else {
    return inputString;
  }
}

export function formatDateTime(dateTime) {
  const jsDate = moment("1900-01-01").add(dateTime, "days").toDate();
  return moment(jsDate).format("DD:MM:YYYY hh:mm a");
}

/**
 * From a Google Geocode API response, derive a short suburb/locality string for display.
 * Falls back to a segment of formatted_address, then to coordinates.
 * @param {{ status: string, results?: Array }} geocodeData - Response from geocode/json?latlng=...
 * @param {number} lat
 * @param {number} lng
 * @returns {string} Suburb name, locality, short address, or "lat, lng"
 */
export function getSuburbFromGeocode(geocodeData, lat, lng) {
  const coordsStr = `${Number(lat).toFixed(4)}, ${Number(lng).toFixed(4)}`;
  if (!geocodeData || geocodeData.status !== "OK" || !Array.isArray(geocodeData.results) || geocodeData.results.length === 0) {
    return coordsStr;
  }
  const result = geocodeData.results[0];
  const components = result.address_components;
  if (components && components.length > 0) {
    const typesOrder = [
      "locality",
      "postal_town",
      "sublocality",
      "sublocality_level_1",
      "neighborhood",
      "administrative_area_level_2",
      "administrative_area_level_1",
    ];
    for (const type of typesOrder) {
      const comp = components.find((c) => c.types && c.types.includes(type));
      if (comp && comp.long_name) {
        return comp.long_name;
      }
    }
  }
  const formatted = result.formatted_address;
  if (formatted && typeof formatted === "string") {
    const parts = formatted.split(",").map((s) => s.trim()).filter(Boolean);
    if (parts.length >= 2) {
      const suburbPart = parts[1];
      if (suburbPart.length > 0 && suburbPart.length <= 80) {
        return suburbPart;
      }
    }
    return formatted;
  }
  return coordsStr;
}

const NOMINATIM_USER_AGENT = "UmphireAdmin/1.0 (location display)";

// Global cache so we never re-request the same coords (shared across admin list, master list, modal)
const reverseGeocodeCache = {};

// Nominatim allows 1 request per second; queue requests and process one every 1.1s
const nominatimQueue = [];
let nominatimBusy = false;
const NOMINATIM_INTERVAL_MS = 1100;

function processNominatimQueue() {
  if (nominatimBusy || nominatimQueue.length === 0) return;
  const item = nominatimQueue.shift();
  nominatimBusy = true;
  const coordsStr = `${Number(item.lat).toFixed(4)}, ${Number(item.lng).toFixed(4)}`;
  fetch(
    `https://nominatim.openstreetmap.org/reverse?lat=${item.lat}&lon=${item.lng}&format=json`,
    { headers: { "User-Agent": NOMINATIM_USER_AGENT } }
  )
    .then((res) => res.json())
    .then((data) => item.resolve(getSuburbFromNominatim(data, item.lat, item.lng)))
    .catch(() => item.resolve(coordsStr))
    .finally(() => {
      nominatimBusy = false;
      if (nominatimQueue.length > 0) {
        setTimeout(processNominatimQueue, NOMINATIM_INTERVAL_MS);
      }
    });
}

function fetchNominatimQueued(lat, lng) {
  return new Promise((resolve) => {
    const coordsStr = `${Number(lat).toFixed(4)}, ${Number(lng).toFixed(4)}`;
    nominatimQueue.push({ lat, lng, resolve });
    if (!nominatimBusy) processNominatimQueue();
  });
}

/**
 * Parse OpenStreetMap Nominatim reverse-geocode response into a suburb/locality string.
 * @param {object} data - Response from nominatim.openstreetmap.org/reverse
 * @param {number} lat
 * @param {number} lng
 * @returns {string} Suburb, city, or short place name; or "lat, lng"
 */
export function getSuburbFromNominatim(data, lat, lng) {
  const coordsStr = `${Number(lat).toFixed(4)}, ${Number(lng).toFixed(4)}`;
  if (!data || typeof data !== "object") return coordsStr;
  const addr = data.address;
  if (addr && typeof addr === "object") {
    const name =
      addr.suburb ||
      addr.village ||
      addr.city ||
      addr.town ||
      addr.municipality ||
      addr.county ||
      addr.state_district ||
      addr.state;
    if (name) return name;
  }
  const displayName = data.display_name;
  if (displayName && typeof displayName === "string") {
    const parts = displayName.split(",").map((s) => s.trim()).filter(Boolean);
    if (parts.length >= 1 && parts[0].length <= 80) return parts[0];
    if (parts.length >= 2 && parts[1].length <= 80) return parts[1];
    return displayName.length <= 80 ? displayName : displayName.slice(0, 77) + "...";
  }
  return coordsStr;
}

/**
 * Resolve lat/lng to a suburb/locality string. Uses global cache first; then Google if key set;
 * then Nominatim (queued 1/sec). Returns coordinates if both fail.
 * @param {number} lat
 * @param {number} lng
 * @param {string} googleApiKey - Optional; if set and API enabled, Google is tried first
 * @returns {Promise<string>} Suburb name or "lat, lng"
 */
export async function reverseGeocodeToSuburb(lat, lng, googleApiKey) {
  const coordsStr = `${Number(lat).toFixed(4)}, ${Number(lng).toFixed(4)}`;
  if (Number.isNaN(Number(lat)) || Number.isNaN(Number(lng))) return coordsStr;

  const cacheKey = `${lat},${lng}`;
  const cached = reverseGeocodeCache[cacheKey];
  if (cached !== undefined) {
    return typeof cached.then === "function" ? cached : Promise.resolve(cached);
  }

  const tryGoogle = async () => {
    if (!googleApiKey || !googleApiKey.trim()) return null;
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${googleApiKey}`
      );
      const data = await response.json();
      if (data && data.status === "OK" && Array.isArray(data.results) && data.results.length > 0) {
        return getSuburbFromGeocode(data, lat, lng);
      }
    } catch (_) {
      /* ignore */
    }
    return null;
  };

  const promise = (async () => {
    const fromGoogle = await tryGoogle();
    if (fromGoogle && fromGoogle !== coordsStr) return fromGoogle;
    return await fetchNominatimQueued(lat, lng);
  })();
  promise.then((result) => {
    reverseGeocodeCache[cacheKey] = result;
  });
  reverseGeocodeCache[cacheKey] = promise;
  return promise;
}

/** API tinyint/boolean flags (0, 1, "0", "1", true, false). */
export function apiFlagIsTrue(val) {
  return val === true || val === 1 || val === "1";
}

/** Master org: show join-request UI unless API explicitly sets show_community_join_requests to false. */
export function shouldShowCommunityJoinRequests(...sources) {
  return !sources.some((s) => s && s.show_community_join_requests === false);
}

/** Roles excluded from master-org member counts (matches Members page). */
export const MASTER_ORG_EXCLUDED_USER_ROLES = [
  "schools",
  "individual",
  "master_org",
  "sub_org",
  "master",
];

/** Filter master-org users list to actual members (same rules as Members page). */
export function filterMasterOrgMembers(users) {
  if (!Array.isArray(users)) return [];
  return users.filter((u) => !MASTER_ORG_EXCLUDED_USER_ROLES.includes(u.role));
}

/** Active members only — matches org website community members list. */
export function filterActiveMasterOrgMembers(users) {
  return filterMasterOrgMembers(users).filter((u) => u.status === "active");
}

/** Member totals from master-org users API response (active members only). */
export function countMasterOrgMembersFromResponse(response) {
  const activeMembers = filterActiveMasterOrgMembers(response?.data);
  const active =
    activeMembers.length > 0 || !response?.count
      ? activeMembers.length
      : response.count;
  return { total: active, active };
}

// Format date and time values in the array

